import type { WorkHours, DayKey } from '../lib/types';

const DAYS: { key: DayKey; label: string }[] = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];

const TZ_OPTIONS = [
  'Asia/Singapore',
  'Asia/Bangkok',
  'Asia/Jakarta',
  'Asia/Kuala_Lumpur',
  'Asia/Dubai',
  'Asia/Riyadh',
  'Europe/London',
  'America/New_York',
  'America/Los_Angeles',
];

interface Props {
  value: WorkHours | null;
  onChange: (next: WorkHours | null) => void;
}

const DEFAULT_OPEN = 9;
const DEFAULT_CLOSE = 18;

export const WorkHoursEditor = ({ value, onChange }: Props) => {
  const tz = value?.tz ?? 'Asia/Singapore';
  const weekdays = value?.weekdays ?? {};

  const update = (next: Partial<WorkHours>) => onChange({ tz, weekdays, ...next });

  const setDay = (key: DayKey, hours: [number, number] | null) => {
    update({ weekdays: { ...weekdays, [key]: hours } });
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="label">Timezone</label>
        <select
          className="input mt-1.5"
          value={tz}
          onChange={(e) => update({ tz: e.target.value })}
        >
          {TZ_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {DAYS.map(({ key, label }) => {
          const hours = weekdays[key];
          const open = hours?.[0] ?? DEFAULT_OPEN;
          const close = hours?.[1] ?? DEFAULT_CLOSE;
          const closed = hours == null;
          return (
            <div
              key={key}
              className="flex items-center gap-2 border border-line rounded-md px-3 py-2 bg-white"
            >
              <div className="w-10 text-xs text-ink-soft font-medium">{label}</div>
              <label className="flex items-center gap-1.5 text-xs text-ink-muted select-none">
                <input
                  type="checkbox"
                  checked={!closed}
                  onChange={(e) => setDay(key, e.target.checked ? [open, close] : null)}
                />
                Open
              </label>
              {!closed && (
                <>
                  <HourInput
                    value={open}
                    onChange={(h) => setDay(key, [Math.min(h, close - 1), close])}
                  />
                  <span className="text-ink-muted text-xs">–</span>
                  <HourInput
                    value={close}
                    onChange={(h) => setDay(key, [open, Math.max(h, open + 1)])}
                  />
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const HourInput = ({ value, onChange }: { value: number; onChange: (h: number) => void }) => (
  <input
    type="number"
    min={0}
    max={24}
    value={value}
    onChange={(e) => onChange(Math.max(0, Math.min(24, parseInt(e.target.value || '0', 10))))}
    className="w-14 input py-1 text-sm text-center"
  />
);
